import React, { useEffect, useState } from "react";
import { apiFetch } from "./api";

const ROLE_LABELS = {
  user: "Kasutaja",
  manager: "Haldur",
  admin: "Administraator",
};

export default function Users({ account, onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());

  // Add-user modal
  const [showAdd, setShowAdd] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  // Password reset modal
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  function loadUsers() {
    setLoading(true);
    setError("");
    apiFetch("/api/users")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to load users", err);
        setError("Kasutajate laadimine ebaõnnestus.");
      })
      .finally(() => setLoading(false));
  }

  function setBusy(id, on) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });
  }

  async function updateUser(user, changes, okMessage) {
    setBusy(user.id, true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await apiFetch(`/api/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Salvestamine ebaõnnestus (HTTP ${res.status})`);
      setSuccessMessage(okMessage);
      loadUsers();
    } catch (err) {
      console.error("Failed to update user", err);
      setError(err.message || "Salvestamine ebaõnnestus.");
    } finally {
      setBusy(user.id, false);
    }
  }

  async function createUser() {
    const username = newUsername.trim();
    if (!username || !newPassword) return;

    setAdding(true);
    setAddError("");
    try {
      const res = await apiFetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password: newPassword,
          email: newEmail.trim() || null,
          role: newRole,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Lisamine ebaõnnestus (HTTP ${res.status})`);

      setShowAdd(false);
      setNewUsername("");
      setNewEmail("");
      setNewPassword("");
      setNewRole("user");
      setSuccessMessage(`Kasutaja lisatud: ${username}`);
      loadUsers();
    } catch (err) {
      console.error("Failed to create user", err);
      setAddError(err.message || "Lisamine ebaõnnestus.");
    } finally {
      setAdding(false);
    }
  }

  async function doResetPassword() {
    if (!resetUser || !resetPassword) return;
    setResetting(true);
    setResetError("");
    try {
      const res = await apiFetch(`/api/users/${resetUser.id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: resetPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Muutmine ebaõnnestus (HTTP ${res.status})`);

      setResetUser(null);
      setResetPassword("");
      setSuccessMessage(`Parool muudetud: ${resetUser.username}`);
    } catch (err) {
      console.error("Failed to reset password", err);
      setResetError(err.message || "Parooli muutmine ebaõnnestus.");
    } finally {
      setResetting(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Kasutajad</h1>
          <div style={styles.subtitle}>
            Lisa kasutajaid, määra rolle ja muuda paroole.
          </div>
        </div>

        <div style={styles.topActions}>
          <button
            onClick={() => {
              setAddError("");
              setShowAdd(true);
            }}
            style={styles.primaryBtn}
            type="button"
          >
            Lisa kasutaja
          </button>
          <button onClick={onBack} style={styles.secondaryBtn} type="button">
            ← Tagasi
          </button>
        </div>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.infoText}>Laen kasutajaid…</div>
        ) : (
          <>
            {error && <div style={styles.errorText}>{error}</div>}
            {successMessage && <div style={styles.successText}>{successMessage}</div>}

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Kasutajanimi</th>
                    <th style={styles.th}>E-post</th>
                    <th style={styles.th}>Roll</th>
                    <th style={styles.th}>Staatus</th>
                    <th style={styles.th}>Tegevus</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isSelf = u.username === account?.username;
                    const busy = busyIds.has(u.id);
                    return (
                      <tr key={u.id}>
                        <td style={styles.td}>
                          {u.username}
                          {isSelf && <span style={styles.selfTag}>sina</span>}
                        </td>
                        <td style={styles.td}>{u.email || "—"}</td>
                        <td style={styles.td}>
                          <select
                            value={u.role}
                            disabled={isSelf || busy}
                            onChange={(e) =>
                              updateUser(
                                u,
                                { role: e.target.value },
                                `Roll muudetud: ${u.username}`
                              )
                            }
                            style={isSelf ? styles.selectDisabled : styles.select}
                            title={isSelf ? "Oma rolli ei saa muuta" : ""}
                          >
                            {Object.entries(ROLE_LABELS).map(([value, labelText]) => (
                              <option key={value} value={value}>
                                {labelText}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={styles.td}>
                          <span style={u.is_active ? styles.badgeOn : styles.badgeOff}>
                            {u.is_active ? "Aktiivne" : "Deaktiveeritud"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.rowActions}>
                            <button
                              type="button"
                              style={styles.smallBtn}
                              disabled={busy}
                              onClick={() => {
                                setResetError("");
                                setResetPassword("");
                                setResetUser(u);
                              }}
                            >
                              Muuda parool
                            </button>

                            <button
                              type="button"
                              style={isSelf ? styles.disabledBtn : styles.smallBtn}
                              disabled={isSelf || busy}
                              title={isSelf ? "Iseennast ei saa deaktiveerida" : ""}
                              onClick={() =>
                                updateUser(
                                  u,
                                  { is_active: !u.is_active },
                                  u.is_active
                                    ? `Deaktiveeritud: ${u.username}`
                                    : `Aktiveeritud: ${u.username}`
                                )
                              }
                            >
                              {u.is_active ? "Deaktiveeri" : "Aktiveeri"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add user modal */}
      {showAdd && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Lisa kasutaja</h3>

            <label style={styles.label}>Kasutajanimi</label>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              autoFocus
              style={styles.modalInput}
            />

            <label style={styles.label}>E-post</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="valikuline"
              style={styles.modalInput}
            />

            <label style={styles.label}>Parool (vähemalt 8 tähemärki)</label>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.modalInput}
            />

            <label style={styles.label}>Roll</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              style={styles.modalInput}
            >
              {Object.entries(ROLE_LABELS).map(([value, labelText]) => (
                <option key={value} value={value}>
                  {labelText}
                </option>
              ))}
            </select>

            {addError && <div style={styles.errorText}>{addError}</div>}

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                style={styles.secondaryBtn}
                disabled={adding}
              >
                Tühista
              </button>
              <button
                type="button"
                onClick={createUser}
                disabled={adding || !newUsername.trim() || newPassword.length < 8}
                style={
                  adding || !newUsername.trim() || newPassword.length < 8
                    ? styles.disabledBtn
                    : styles.primaryBtn
                }
              >
                {adding ? "Lisan…" : "Lisa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {resetUser && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Muuda parool — {resetUser.username}</h3>

            <label style={styles.label}>Uus parool (vähemalt 8 tähemärki)</label>
            <input
              type="text"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              autoFocus
              style={styles.modalInput}
            />

            {resetError && <div style={styles.errorText}>{resetError}</div>}

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setResetUser(null)}
                style={styles.secondaryBtn}
                disabled={resetting}
              >
                Tühista
              </button>
              <button
                type="button"
                onClick={doResetPassword}
                disabled={resetting || resetPassword.length < 8}
                style={
                  resetting || resetPassword.length < 8
                    ? styles.disabledBtn
                    : styles.primaryBtn
                }
              >
                {resetting ? "Salvestan…" : "Salvesta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F3F6FD",
    fontFamily: "'DM Sans', sans-serif",
    padding: "28px",
  },
  topBar: {
    maxWidth: "1100px",
    margin: "0 auto 20px auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  topActions: { display: "flex", gap: "10px", alignItems: "center" },
  title: { margin: 0, color: "#0D1F4D", fontSize: "28px" },
  subtitle: { marginTop: "6px", color: "#555", fontSize: "14px" },
  card: {
    maxWidth: "1100px",
    margin: "0 auto",
    background: "white",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: "1px solid #E6ECF8",
    color: "#0D1F4D",
    fontWeight: 700,
    fontSize: "14px",
    background: "#F8FAFF",
  },
  td: {
    padding: "12px 10px",
    borderBottom: "1px solid #E6ECF8",
    fontSize: "14px",
    color: "#0D1F4D",
    verticalAlign: "middle",
  },
  selfTag: {
    marginLeft: "8px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#6C7A9A",
    background: "#F3F6FD",
    border: "1px solid #E6ECF8",
    borderRadius: "999px",
    padding: "2px 8px",
  },
  select: {
    borderRadius: "10px",
    border: "1px solid #E6ECF8",
    padding: "8px 10px",
    background: "#fff",
    outline: "none",
    minWidth: "160px",
  },
  selectDisabled: {
    borderRadius: "10px",
    border: "1px solid #E6ECF8",
    padding: "8px 10px",
    background: "#F3F6FD",
    color: "#6C7A9A",
    outline: "none",
    minWidth: "160px",
    cursor: "not-allowed",
  },
  badgeOn: {
    background: "#E7F6EC",
    border: "1px solid #B6E2C6",
    color: "#1E8449",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 700,
  },
  badgeOff: {
    background: "#FDECEA",
    border: "1px solid #F5C6C0",
    color: "#C0392B",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 700,
  },
  rowActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  smallBtn: {
    background: "#F3F6FD",
    border: "1px solid #E6ECF8",
    borderRadius: "8px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 700,
    color: "#0D1F4D",
    fontSize: "12px",
  },
  primaryBtn: {
    backgroundColor: "#0D1F4D",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    backgroundColor: "#8CA5E6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  disabledBtn: {
    backgroundColor: "#D8E1F7",
    color: "#6C7A9A",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
  infoText: { color: "#555", padding: "12px 4px" },
  errorText: { color: "#C0392B", padding: "8px 0", fontWeight: 600, fontSize: "13px" },
  successText: { color: "#1E8449", padding: "0 0 12px 0", fontWeight: 600 },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "white",
    borderRadius: "14px",
    padding: "24px",
    width: "400px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  },
  modalTitle: { margin: "0 0 16px 0", color: "#0D1F4D", fontSize: "18px" },
  label: {
    display: "block",
    fontWeight: 600,
    color: "#0D1F4D",
    marginBottom: "6px",
    fontSize: "13px",
  },
  modalInput: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "10px",
    border: "1px solid #8CA5E6",
    padding: "10px 12px",
    outline: "none",
    background: "#fff",
    fontSize: "14px",
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: "14px",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "12px",
  },
};
